import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/features/auth';
import { useDeleteBid, useUpdateBid } from '@/features/bids/mutations';
import { BID_TYPES, onlyBidCannotBeDeleted } from '@/features/bids/constants';
import { isOnlyWorkspaceBid } from '@/features/bids/utils';
import type { BidType } from '@/features/bids/types';
import { Select, SelectItem, SelectPopup, SelectTrigger } from '@/ui/components/select';
import { Field, FieldControl, FieldError, FieldLabel } from '@/ui/components/form/field';
import { Button } from '@/ui/components/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
} from '@/ui/components/alert-dialog';
import { toastManager } from '@/ui/components/toast';
import { useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { bidDetailsQuery, bidsQuery } from '@/features/bids/queries';
import { companiesQuery } from '@/features/companies/queries';
import { CompanyType } from '@/features/companies/types';
import { Form } from '@/ui/components/form';
import { useProjectView } from '@/features/projects/helpers';
import { CompanyCombobox } from '@/features/companies/components/company-combobox';
import { Card } from '@/ui/components/card';
import { H3 } from '@/ui/components/heading';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/bids/$bidId/_layout/settings/',
)({
  component: RouteComponent,
  remountDeps: ({ params }) => params.bidId,
  loader: async ({ context }) => {
    await context.queryClient.query({
      ...companiesQuery(context.auth.user.organizationId, CompanyType.SUPPLIER),
      staleTime: 'static',
    });
  },
});

function RouteComponent() {
  const { bidId, workspaceId, projectId } = Route.useParams();
  const auth = useAuth();
  const navigate = useNavigate();

  const view = useProjectView();
  const isClientView = view === 'client';
  const { data: bid } = useSuspenseQuery(bidDetailsQuery(bidId));
  const { data: bids } = useSuspenseQuery(bidsQuery(workspaceId));
  const { data: companies } = useSuspenseQuery(
    companiesQuery(auth.user.organizationId, CompanyType.SUPPLIER),
  );
  const updateBid = useUpdateBid();
  const deleteMutation = useDeleteBid();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bidAdditionalName, setBidAdditionalName] = useState(bid.bidAdditionalName ?? '');
  const [companyId, setCompanyId] = useState(bid.companyId ?? null);
  const [bidType, setBidType] = useState(bid.bidType);

  return (
    <Form
      onFormSubmit={() => {
        if (!bidType) return;
        updateBid.mutate(
          {
            companyId,
            additionalInfo: bid.additionalInfo,
            bidAdditionalName,
            bidType,
          },
          {
            onSuccess: () =>
              toastManager.add({
                type: 'success',
                title: t(common.notifications.updatedSuccessfully),
              }),
          },
        );
      }}
      className="max-w-xl w-full flex flex-col gap-4"
    >
      <Card className="space-y-4 p-4">
        <H3 className="-mt-1">
          {t({
            en: view === 'client' ? 'Submission information' : 'Bid information',
            nb: view === 'client' ? 'Innleveringsinformasjon' : 'Tilbudsinformasjon',
          })}
        </H3>
        <Field>
          <FieldLabel>{t({ en: 'Name', nb: 'Navn' })}</FieldLabel>
          <FieldControl
            value={bidAdditionalName}
            onValueChange={setBidAdditionalName}
            placeholder={t({ en: 'Name', nb: 'Navn' })}
            maxLength={60}
            disabled={updateBid.isPending}
          />
          <FieldError />
        </Field>
        <Field>
          <FieldLabel>
            {t({
              en: view === 'client' ? 'Submission type' : 'Bid type',
              nb: view === 'client' ? 'Innleveringstype' : 'Tilbudstype',
            })}
          </FieldLabel>
          <Select
            required
            value={bidType}
            onValueChange={(val) => setBidType((val as BidType) || null)}
            disabled={updateBid.isPending}
          >
            <SelectTrigger format={(val) => (val ? t(common.bidTypes[val as BidType]) : '')} />
            <SelectPopup>
              {BID_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(common.bidTypes[type])}
                </SelectItem>
              ))}
            </SelectPopup>
          </Select>
          <FieldError />
        </Field>
        {isClientView ? (
          <Field>
            <FieldLabel>{t({ en: 'Supplier', nb: 'Leverandør' })}</FieldLabel>
            <CompanyCombobox
              companies={companies}
              value={companies.find((company) => company.id === companyId) ?? null}
              onValueChange={(company) => setCompanyId(company?.id ?? null)}
              disabled={updateBid.isPending}
              ariaLabel={t({ en: 'Supplier', nb: 'Leverandør' })}
              type={CompanyType.SUPPLIER}
            />
            <FieldError />
          </Field>
        ) : null}
      </Card>
      <Button
        type="submit"
        pending={updateBid.isPending}
        disabled={updateBid.isPending}
        className="ml-auto"
      >
        {t({ en: 'Save', nb: 'Lagre' })}
      </Button>
      <Card className="p-4">
        <H3 className="-mt-1">
          {t({
            en: view === 'client' ? 'Delete submission' : 'Delete bid',
            nb: view === 'client' ? 'Slett innlevering' : 'Slett tilbud',
          })}
        </H3>
        <p className="text-sm text-muted mt-2 mb-4">
          {t({
            en:
              view === 'client'
                ? 'Woah, approach with caution! There is no going back when you delete a submission.'
                : 'Woah, approach with caution! There is no going back when you delete a bid.',
            nb:
              view === 'client'
                ? 'Wow, vær forsiktig! Det er ingen vei tilbake når du sletter en innlevering.'
                : 'Wow, vær forsiktig! Det er ingen vei tilbake når du sletter et tilbud.',
          })}
        </p>
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <Button
            variant="destructive"
            onClick={() => {
              if (isOnlyWorkspaceBid(view, bids.data.length))
                return toastManager.add({
                  type: 'error',
                  title: t(onlyBidCannotBeDeleted),
                });
              setDeleteDialogOpen(true);
            }}
          >
            {t({
              en: view === 'client' ? 'Delete submission' : 'Delete bid',
              nb: view === 'client' ? 'Slett innlevering' : 'Slett tilbud',
            })}
          </Button>
          <AlertDialogPopup>
            <AlertDialogTitle>
              {t({
                en: view === 'client' ? 'Delete submission?' : 'Delete bid?',
                nb: view === 'client' ? 'Slette innlevering?' : 'Slette tilbud?',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t({
                en:
                  view === 'client'
                    ? 'Are you sure you want to remove this submission from the tender? This action cannot be undone.'
                    : 'Are you sure you want to remove this bid from the tender? This action cannot be undone.',
                nb:
                  view === 'client'
                    ? 'Er du sikker på at du vil fjerne denne innleveringen fra anbudet? Denne handlingen kan ikke angres.'
                    : 'Er du sikker på at du vil fjerne dette tilbudet fra anbudet? Denne handlingen kan ikke angres.',
              })}
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel />
              <Button
                variant="destructive"
                onClick={() =>
                  deleteMutation.mutate(
                    { bidId, companyName: bid.companyName ?? '' },
                    {
                      onSuccess: () => {
                        setDeleteDialogOpen(false);
                        navigate({
                          to: '/project/$projectId/workspaces/$workspaceId/bids',
                          params: { projectId, workspaceId },
                        });
                      },
                    },
                  )
                }
                pending={deleteMutation.isPending}
                disabled={deleteMutation.isPending}
              >
                {t({
                  en: view === 'client' ? 'Delete submission' : 'Delete bid',
                  nb: view === 'client' ? 'Slett innlevering' : 'Slett tilbud',
                })}
              </Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      </Card>
    </Form>
  );
}
